import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { UserProfilePanel } from "../UserProfilePanel";
import { AppDispatch, RootState } from "@/state/store";
import { fetchUserProfileThunk, updateUserProfileThunk } from "@/state/userProfile/userProfileSlice";
import { UserProfiles } from "@/api";

export interface UserProfileProps {
    isOpen: boolean;
    onClose: () => void;
    userName: string;
    isProfileUpdate: boolean;
}

export const UserProfile = ({ isOpen, onClose, userName, isProfileUpdate }: UserProfileProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const userId = useSelector((state: RootState) => state.userProfiles.userProfile?.userid);
    const currentUserProfile = useSelector((state: RootState) => state.userProfiles.userProfile);
    const userProfileStatus = useSelector((state: RootState) => state.userProfiles.status);
    const error = useSelector((state: RootState) => state.userProfiles.error);

    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(isOpen);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [profileErrorMessage, setProfileErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) {
            setIsPanelOpen(true);
        } else {
            setIsPanelOpen(false);
            onCloseProfilePanel();
        }
    }, [isOpen]);

    const onCloseProfilePanel = () => {
        onClose();
        setIsPanelOpen(false);
        setProfileErrorMessage(null);
    };

    const submitUserProfile = (profileData: UserProfiles, onSuccess: () => void) => {
        setIsSaving(true);
        setProfileErrorMessage(null);

        dispatch(updateUserProfileThunk(profileData))
            .unwrap()
            .then(() => {
                onSuccess();
            })
            .catch(error => {
                setProfileErrorMessage("Error encountered while updating. Please try again.");
                console.error("Error updating profile:", error);
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const onFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (userId === undefined) {
            setProfileErrorMessage("User ID not found. Please try again.");
            return;
        }

        const profileData: UserProfiles = {
            userid: userId,
            firstName: (e.currentTarget.elements.namedItem("firstName") as HTMLInputElement)?.value || "",
            lastName: (e.currentTarget.elements.namedItem("lastName") as HTMLInputElement)?.value || "",
            companyName: (e.currentTarget.elements.namedItem("companyName") as HTMLInputElement)?.value || "",
            companydesc: (e.currentTarget.elements.namedItem("companydesc") as HTMLInputElement)?.value || "",
            industry: (e.currentTarget.elements.namedItem("industry") as HTMLInputElement)?.value || "",
            department: (e.currentTarget.elements.namedItem("department") as HTMLInputElement)?.value || "",
            role: (e.currentTarget.elements.namedItem("role") as HTMLInputElement)?.value || "",
            username: userName,
            projectid: currentUserProfile?.projectid || 0,
            activeVersion: currentUserProfile?.activeVersion || "",
            settings: currentUserProfile?.settings || { ActiveProjectId: 0, ActiveWorkProductId: 0, ActiveVersion: [] },
            preferences: currentUserProfile?.preferences || {},
            newuser: currentUserProfile?.newuser || false,
            newprofile: currentUserProfile?.newprofile || false
        };

        submitUserProfile(profileData, onCloseProfilePanel);
    };

    return isOpen ? (
        <div>
            <UserProfilePanel
                isSaving={isSaving}
                isOpen={isPanelOpen}
                onClose={onCloseProfilePanel}
                onFormSubmit={onFormSubmit}
                isProfileUpdate={isProfileUpdate}
                profileErrorMessage={profileErrorMessage || error || ""}
            />
        </div>
    ) : null;
};
